<template>
  <AdminAppLayout title="HRDOS | Link Employee">
    <div class="card z-depth-1" style="padding: 40px; border-radius: 30px;">
      <h5 class="center-align" style="margin-bottom: 6px">Link Employee</h5>
      <p class="center-align grey-text" style="margin-bottom: 30px; max-width: 600px; margin-left: auto; margin-right: auto;">
        For a login that already exists (created outside the normal Add Employee flow) but has no 201 profile
        attached to it yet. Pick the account, fill in their details, and this creates the employee record and
        links it to that login — no new username/email/password needed.
      </p>

      <!-- Loading unlinked users -->
      <div v-if="loadingUsers" class="center-align" style="padding: 30px;">
        <div class="preloader-wrapper small active">
          <div class="spinner-layer spinner-blue-only">
            <div class="circle-clipper left"><div class="circle"></div></div>
            <div class="gap-patch"><div class="circle"></div></div>
            <div class="circle-clipper right"><div class="circle"></div></div>
          </div>
        </div>
      </div>

      <!-- Nothing to link -->
      <div v-else-if="!unlinkedUsers.length" class="center-align" style="padding: 30px; color: #757575;">
        <i class="material-icons" style="font-size: 40px; color: #bbb;">task_alt</i>
        <p>No accounts are missing an employee record right now.</p>
      </div>

      <form v-else @submit.prevent="submit">
        <div class="row">
          <div class="input-field col s12 m8 offset-m2">
            <select id="user_id" v-model="form.user_id" required>
              <option value="" disabled selected>Choose the account to link</option>
              <option v-for="u in unlinkedUsers" :key="u.id" :value="u.id">
                {{ u.username }} — {{ u.email }}
              </option>
            </select>
            <label>Login Account</label>
          </div>
        </div>

        <div v-if="form.user_id" class="row" style="margin-top: 10px;">
          <!-- Photo -->
          <div class="file-field input-field col s12 m8 offset-m2">
            <div class="btn blue lighten-1">
              <span>Photo</span>
              <input type="file" accept="image/jpeg,image/png" @change="handlePhotoUpload">
            </div>
            <div class="file-path-wrapper">
              <input class="file-path" type="text" placeholder="Optional profile photo" readonly>
            </div>
          </div>

          <!-- Name -->
          <div class="input-field col s12 m4">
            <input id="first_name" type="text" v-model="employee.first_name" required>
            <label for="first_name" class="active">First Name</label>
            <span class="helper-text red-text">{{ errors.first_name }}</span>
          </div>
          <div class="input-field col s12 m4">
            <input id="middle_name" type="text" v-model="employee.middle_name" required>
            <label for="middle_name" class="active">Middle Name</label>
            <span class="helper-text red-text">{{ errors.middle_name }}</span>
          </div>
          <div class="input-field col s12 m4">
            <input id="last_name" type="text" v-model="employee.last_name" required>
            <label for="last_name" class="active">Last Name</label>
            <span class="helper-text red-text">{{ errors.last_name }}</span>
          </div>

          <!-- Birthday / Sex / Cellphone -->
          <div class="input-field col s12 m4">
            <input id="birthday" type="date" v-model="employee.birthday" required>
            <label for="birthday" class="active">Birthday</label>
            <span class="helper-text red-text">{{ errors.birthday }}</span>
          </div>
          <div class="input-field col s12 m4">
            <select id="sex" v-model="employee.sex" required>
              <option value="" disabled selected></option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            <label>Sex</label>
            <span class="helper-text red-text">{{ errors.sex }}</span>
          </div>
          <div class="input-field col s12 m4">
            <input id="cellphone_number" type="text" v-model="employee.cellphone_number" required>
            <label for="cellphone_number" class="active">Cellphone Number</label>
            <span class="helper-text red-text">{{ errors.cellphone_number }}</span>
          </div>

          <!-- Address / Birthplace -->
          <div class="input-field col s12 m6">
            <textarea id="address" class="materialize-textarea" v-model="employee.address" required></textarea>
            <label for="address" class="active">Address</label>
            <span class="helper-text red-text">{{ errors.address }}</span>
          </div>
          <div class="input-field col s12 m6">
            <textarea id="birthplace" class="materialize-textarea" v-model="employee.birthplace" required></textarea>
            <label for="birthplace" class="active">Birthplace</label>
            <span class="helper-text red-text">{{ errors.birthplace }}</span>
          </div>

          <h6 class="col s12" style="margin-top: 20px; color: #616161;">Employment Details</h6>

          <div class="input-field col s12 m6">
            <select id="employment_status_id" v-model="employee.employment_status_id" required>
              <option value="" disabled selected></option>
              <option v-for="s in dropdowns.employmentStatuses" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <label>Employment Status</label>
            <span class="helper-text red-text">{{ errors.employment_status_id }}</span>
          </div>
          <div class="input-field col s12 m6">
            <select id="position_id" v-model="employee.position_id" @change="loadSalaryGradeFromPosition" required>
              <option value="" disabled selected></option>
              <option v-for="p in dropdowns.positions" :key="p.id" :value="p.id">{{ p.title }}</option>
            </select>
            <label>Position</label>
            <span class="helper-text red-text">{{ errors.position_id }}</span>
          </div>

          <div class="input-field col s12 m6">
            <select id="plantilla_id" v-model="employee.plantilla_id" required>
              <option value="" disabled selected></option>
              <option v-for="p in dropdowns.plantillas" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
            <label>Plantilla</label>
            <span class="helper-text red-text">{{ errors.plantilla_id }}</span>
          </div>
          <div class="input-field col s12 m6">
            <select id="eligibility_id" v-model="employee.eligibility_id" required>
              <option value="" disabled selected></option>
              <option v-for="e in dropdowns.eligibilities" :key="e.id" :value="e.id">{{ e.name }}</option>
            </select>
            <label>Eligibility</label>
            <span class="helper-text red-text">{{ errors.eligibility_id }}</span>
          </div>

          <div class="input-field col s12 m6">
            <select id="assigned_office_id" v-model="employee.assigned_office_id" required>
              <option value="" disabled selected></option>
              <option v-for="o in dropdowns.offices" :key="o.id" :value="o.id">{{ o.name }}</option>
            </select>
            <label>Assigned Office</label>
            <span class="helper-text red-text">{{ errors.assigned_office_id }}</span>
          </div>
          <div class="input-field col s12 m6">
            <input id="effectivity_date" type="date" v-model="employee.effectivity_date" required>
            <label for="effectivity_date" class="active">Effectivity Date</label>
            <span class="helper-text red-text">{{ errors.effectivity_date }}</span>
          </div>

          <div class="input-field col s12 m6">
            <input id="employee_id_number" type="text" v-model="employee.employee_id_number" required>
            <label for="employee_id_number" class="active">
              Employee ID Number
              <span class="grey-text" style="font-size: 0.8rem;">(without the LTO-RO2-/LTO-COS- prefix — added automatically)</span>
            </label>
            <span class="helper-text red-text">{{ errors.employee_id_number }}</span>
          </div>
          <div class="input-field col s12 m6" v-if="employee.employment_status_id != 3">
            <input id="item_number" type="text" v-model="employee.item_number" required>
            <label for="item_number" class="active">Item Number</label>
            <span class="helper-text red-text">{{ errors.item_number }}</span>
          </div>

          <!-- Salary -->
          <template v-if="employee.employment_status_id == 3">
            <div class="input-field col s12 m6">
              <input id="base_salary" type="number" step="0.01" v-model="employee.base_salary" required>
              <label for="base_salary" class="active">Base Salary (JO/COS)</label>
              <span class="helper-text red-text">{{ errors.base_salary }}</span>
            </div>
          </template>
          <template v-else>
            <div class="input-field col s12 m3">
              <input type="text" :value="employee.salary_grade_display" readonly>
              <label class="active">Salary Grade</label>
            </div>
            <div class="input-field col s12 m3">
              <select id="salary_step" v-model="employee.salary_step" @change="loadSalaryAmount" :disabled="!dropdowns.salarySteps.length">
                <option value="" disabled selected></option>
                <option v-for="s in dropdowns.salarySteps" :key="s.step" :value="s.step">Step {{ s.step }}</option>
              </select>
              <label>Salary Step</label>
            </div>
            <div class="input-field col s12 m6">
              <input type="text" :value="salaryAmount ? formatCurrency(salaryAmount) : ''" readonly>
              <label class="active">Monthly Salary</label>
            </div>
          </template>

          <h6 class="col s12" style="margin-top: 20px; color: #616161;">Government IDs</h6>

          <div class="input-field col s12 m4">
            <input id="tin" type="text" v-model="employee.tin">
            <label for="tin" class="active">TIN</label>
            <span class="helper-text red-text">{{ errors.tin }}</span>
          </div>
          <div class="input-field col s12 m4">
            <input id="gsis" type="text" v-model="employee.gsis">
            <label for="gsis" class="active">GSIS</label>
            <span class="helper-text red-text">{{ errors.gsis }}</span>
          </div>
          <div class="input-field col s12 m4">
            <input id="sss" type="text" v-model="employee.sss">
            <label for="sss" class="active">SSS</label>
            <span class="helper-text red-text">{{ errors.sss }}</span>
          </div>
          <div class="input-field col s12 m6">
            <input id="pagibig" type="text" v-model="employee.pagibig" required>
            <label for="pagibig" class="active">Pag-IBIG</label>
            <span class="helper-text red-text">{{ errors.pagibig }}</span>
          </div>
          <div class="input-field col s12 m6">
            <input id="philhealth" type="text" v-model="employee.philhealth" required>
            <label for="philhealth" class="active">PhilHealth</label>
            <span class="helper-text red-text">{{ errors.philhealth }}</span>
          </div>
        </div>

        <div class="right-align" style="margin-top: 20px;">
          <button type="submit" class="btn green" :disabled="submitting">
            <i class="material-icons left">link</i>
            <b>{{ submitting ? 'Linking...' : 'Link Employee Record' }}</b>
          </button>
        </div>
      </form>
    </div>
  </AdminAppLayout>
</template>

<script>
import { api } from "@/axios";
import AdminAppLayout from "@/views/layout/AdminAppLayout.vue";

export default {
  name: "LinkEmployeeView",
  components: { AdminAppLayout },
  data() {
    return {
      loadingUsers: true,
      submitting: false,
      unlinkedUsers: [],
      form: {
        user_id: "",
      },
      employee: {
        profile_photo: null,
        employee_id_number: "",
        item_number: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        cellphone_number: "",
        address: "",
        birthplace: "",
        birthday: "",
        sex: "",
        employment_status_id: "",
        position_id: "",
        plantilla_id: "",
        eligibility_id: "",
        assigned_office_id: "",
        effectivity_date: "",
        salary_grade_display: "",
        salary_grade: "",
        salary_step: "",
        base_salary: "",
        tin: "",
        gsis: "",
        sss: "",
        pagibig: "",
        philhealth: "",
      },
      errors: {},
      dropdowns: {
        positions: [],
        plantillas: [],
        eligibilities: [],
        employmentStatuses: [],
        offices: [],
        salarySteps: [],
      },
      salaryAmount: "",
    };
  },
  methods: {
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === "") return "0.00";
      return Number(amount).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async fetchUnlinkedUsers() {
      this.loadingUsers = true;
      try {
        const res = await api.get("/employees/unlinked-users");
        this.unlinkedUsers = res.data || [];
      } catch (err) {
        console.error("Failed to load unlinked accounts", err);
        if (typeof window.M !== "undefined") {
          window.M.toast({ html: "Failed to load accounts.", classes: "red rounded" });
        }
      } finally {
        this.loadingUsers = false;
        this.$nextTick(() => {
          if (window.M) window.M.FormSelect.init(document.querySelectorAll("select"));
        });
      }
    },
    async getListForDropdowns() {
      try {
        const res = await api.get("/dropdowns-lists");
        this.dropdowns.positions = res.data.positions;
        this.dropdowns.plantillas = res.data.plantillas;
        this.dropdowns.eligibilities = res.data.eligibilities;
        this.dropdowns.employmentStatuses = res.data.employmentStatuses;
        this.dropdowns.offices = res.data.offices;
      } catch (err) {
        console.error("Failed to load dropdown lists", err);
      } finally {
        this.$nextTick(() => {
          if (window.M) window.M.FormSelect.init(document.querySelectorAll("select"));
        });
      }
    },
    handlePhotoUpload(e) {
      const file = e.target.files[0];
      if (file) this.employee.profile_photo = file;
    },
    loadSalaryGradeFromPosition() {
      const selected = this.dropdowns.positions.find((p) => p.id === this.employee.position_id);
      if (selected) {
        this.employee.salary_grade = selected.salary_grade_level;
        this.employee.salary_grade_display = selected.salary_grade_level;
        this.employee.salary_step = "";
        this.salaryAmount = "";
        this.getSgSteps(this.employee.salary_grade_display);
      }
    },
    async getSgSteps(salaryGrade) {
      try {
        const res = await api.get("/get-salary-steps/" + salaryGrade);
        this.dropdowns.salarySteps = res.data.steps;
      } catch (err) {
        console.error("Failed to load salary steps", err);
      }
      this.$nextTick(() => {
        if (window.M) window.M.FormSelect.init(document.getElementById("salary_step"));
      });
    },
    loadSalaryAmount() {
      const step = this.dropdowns.salarySteps.find(
        (s) => s.grade == this.employee.salary_grade && s.step == this.employee.salary_step
      );
      this.salaryAmount = step ? step.salary_amount : "";
    },
    async submit() {
      this.submitting = true;
      this.errors = {};

      const formData = new FormData();
      formData.append("user_id", this.form.user_id);
      for (let key in this.employee) {
        if (this.employee[key] !== null && this.employee[key] !== undefined) {
          formData.append(key, this.employee[key]);
        }
      }

      try {
        await api.post("/employee/link-existing", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (typeof window.M !== "undefined") {
          window.M.toast({ html: "Employee linked! It now needs the usual validation approval.", classes: "green rounded" });
        }
        this.$router.push("/employees-signup-requests");
      } catch (err) {
        console.error(err.response);
        const msg = err.response?.data?.message;
        const fieldErrors = err.response?.data?.errors;
        if (fieldErrors) {
          for (let field in fieldErrors) {
            this.errors[field] = fieldErrors[field][0];
          }
        }
        if (typeof window.M !== "undefined") {
          window.M.toast({ html: msg || "Failed to link employee.", classes: "red rounded" });
        }
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.fetchUnlinkedUsers();
    this.getListForDropdowns();
  },
};
</script>

<style scoped>
.helper-text.red-text {
  font-size: 0.75rem;
}
</style>
